import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { open_api_with_cookies, registerLogoutHandler } from "../Utils/network";

// Create the AuthContext
const AuthContext = createContext();

// Create the AuthProvider to wrap your application
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [bearerToken, setBearerToken] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    registerLogoutHandler(logout);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await open_api_with_cookies.get("/profiles/me/"); // Backend should return user info if cookie is valid
      console.log("checkAuth - /profiles/me/ response:", response.data);

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204
      ) {
        const userData = response.data;
        const fullName = userData.first_name + " " + userData.last_name;

        setIsAuthenticated(true);
        setUserName(fullName);
        setCurrentUser(userData);

        // Set cookies to ensure UI components have the required data
        Cookies.set("userName", fullName);
        console.log("checkAuth - Setting userName cookie:", fullName);

        if (userData.userprofile?.id) {
          Cookies.set("userId", userData.userprofile.id);
          setUserId(userData.userprofile.id);
          console.log(
            "checkAuth - Setting userId cookie:",
            userData.userprofile.id
          );
        }
        if (userData.expert_info?.id) {
          Cookies.set("expertID", userData.expert_info.id);
          console.log(
            "checkAuth - Setting expertID cookie:",
            userData.expert_info.id
          );
        }

        // Platform Admin is the only dashboard role allowed to see admin data.
        const hasAdminAccess = userData.user_type === 4;
        setIsAdmin(hasAdminAccess);
        Cookies.set("isAdmin", hasAdminAccess.toString());
        console.log(
          "checkAuth - Setting isAdmin cookie:",
          hasAdminAccess.toString(),
          "(user_type:",
          userData.user_type,
          ")"
        );
      } else {
        setIsAuthenticated(false);
        setUserName("");
        setCurrentUser(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("checkAuth error:", error);
      setIsAuthenticated(false);
      setUserName("");
      setUserId("");
      setCurrentUser(null);
      setIsAdmin(false);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const logout = async () => {
    try {
      await open_api_with_cookies.post("/auth/logout/"); // Backend clears cookie
    } catch (err) {
      console.error("Logout failed", err);
    }

    Cookies.remove("token");
    Cookies.remove("userName");
    Cookies.remove("userId");
    Cookies.remove("expertID");
    Cookies.remove("isAdmin");
    Cookies.remove("refresh");
    Cookies.remove("access");
    Cookies.remove("csrftoken");
    Cookies.remove("sessionid");
    setIsAuthenticated(false);
    setUserName("");
    setUserId("");
    setCurrentUser(null);
    setIsAdmin(false);
  };

  // Provide the state and functions to children
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        userName,
        userId,
        bearerToken,
        logout,
        setUserId,
        setBearerToken,
        setUserName,
        checkAuth,
        isAuthLoading,
        currentUser,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext in components
export const useAuth = () => {
  return useContext(AuthContext);
};

export default useAuth;
